import React from 'react';
import sumBy from 'lodash/sumBy';

import { config } from 'data';
import { dateRange, formatter } from 'helpers';
import { useForm, useFormWatch, useLang } from 'hooks';
import { useStatisticsFeesQuery, useStatisticsVolumesQuery } from 'hooks/queries';
import { Highcharts } from 'components/layout';
import { Form, Select } from 'components/ui';
import { DateRange } from 'types/views';

import ChartView from './ChartView';

const initialRange = DateRange.THIS_WEEK;
const initialDates = dateRange.toDates(initialRange);

type FilterValues = {
  range?: DateRange;
};

const initialFilterValues: FilterValues = {
  range: initialRange,
};

const FeeChart = () => {
  const filterForm = useForm<FilterValues>();
  const lang = useLang();

  const currentRange = useFormWatch('range', filterForm);

  const dates = currentRange && dateRange.toDates(currentRange);
  const dateFrom = dates?.[0] ?? initialDates[0];
  const dateTo = dates?.[1] ?? initialDates[1];

  const feesQuery = useStatisticsFeesQuery({ dateFrom, dateTo });
  const volumesQuery = useStatisticsVolumesQuery({ dateFrom, dateTo });

  const feesAmount = formatter.transformFromCents(sumBy(feesQuery.data, 'amount'));
  const paymentsAmount = formatter.transformFromCents(sumBy(volumesQuery.data, 'input'));

  const feesPercent = Math.ceil(feesAmount / (feesAmount + paymentsAmount) * 100) || 100;
  const paymentsPercent = 100 - feesPercent;

  return (
    <ChartView
      title={lang.get('dashboard.chart.fee')}
      currency={config.DEFAULT_CURRENCY}
      filter={(
        <>
          <Form.Item name="range">
            <Select
              placeholder={lang.get('common.filters.range')}
              options={Object.values(DateRange).map((range) => ({
                value: range,
                label: lang.get(`common.dateRanges.${range.toLowerCase()}`),
              }))}
              popupMatchSelectWidth={false}
            />
          </Form.Item>
        </>
      )}
      filterProps={{
        form: filterForm,
        initialValues: initialFilterValues,
      }}
      loading={feesQuery.isLoading || volumesQuery.isLoading}
    >
      <Highcharts
        options={{
          series: [
            {
              type: 'pie',
              borderRadius: 0,
              enableMouseTracking: false,
              innerSize: '50%',
              startAngle: -15,
              size: '100%',
              dataLabels: {
                connectorWidth: 0,
                distance: '-30%',
                position: 'center',
                verticalAlign: 'middle',
              },
              data: [feesPercent, paymentsPercent]
                .sort((a, b) => b - a)
                .map((percent) => [
                  formatter.formatPercent(percent, 0),
                  percent + 0.01,
                ]),
            },
          ],
        }}
      />
    </ChartView>
  );
};

export default FeeChart;
